import { useCreateAnalysis } from '@modules/analysis/hooks/useCreateAnalysis';
import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { getAnalysisTypeDisplayName } from '@modules/dashboard/helpers';
import { AnalysisMetadataInput } from 'generated/graphql';
import { useEffect } from 'react';
import { mapFromEdgeScope } from 'v2/core/repository/edge-server/analysis/fromEdgeAnalysis';
import { EQLProps } from 'v2/core/repository/eql/types';
import { shallow } from 'zustand/shallow';

interface DuplicateAnalysisOptions {
  eqlProps: Omit<EQLProps, 'sourceDetailsMap'>;
  metadata: string;
  eql?: string;
  metadataV2?: AnalysisMetadataInput;
}

export const useDuplicateAnalysis = ({
  eqlProps,
  metadata,
  eql,
  metadataV2,
}: DuplicateAnalysisOptions) => {
  const { title, useCase, scope } = useAnalysisStore(
    (analysis) => ({
      title: analysis.title,
      useCase: analysis.useCase,
      scope: analysis.scope,
    }),
    shallow,
  );

  const name = `Copy of ${title}`;

  const { createAnalysis, isLoading, error } = useCreateAnalysis({
    useCase,
    name,
    scope: mapFromEdgeScope(scope),
    eqlProps,
    metadata,
    eql,
    metadataV2,
  });

  const { showErrorNotification } = useNotificationContext();

  useEffect(() => {
    if (error) {
      showErrorNotification({
        contentComponent: `Failed to duplicate ${getAnalysisTypeDisplayName(
          useCase,
        )}.`,
        props: { isClosable: true },
      });
    }
  }, [error, useCase, showErrorNotification]);

  return {
    createAnalysis,
    isLoading,
    error,
  };
};
